import type { Check, DeclaredStep, EventRow, Receipt, Triptych } from '@console/types'
import { useNavigate, useParams } from 'react-router-dom'
import { formatInstant, formatMoney } from '../format.js'
import { useRun } from '../runs/queries.js'
import { useVerdicts, verdictLabel } from '../reference/queries.js'
import {
  AsOfLine,
  Button,
  DisclosureSection,
  Fingerprint,
  HairlineRule,
  Skeleton,
  Stamp,
  type StampVariant,
} from '../ui/index.js'

/**
 * Reads the run's VERDICT, not its exception lanes.
 *
 * It used to branch on `exceptions.needsADecision`, which the contract does
 * not publish — so after the migration that array is always empty and every
 * run would have stamped "Parked", including confirmed ones.
 */
/**
 * The VARIANT is the console's judgement; the LABEL is the platform's word.
 *
 * These are two different things and used to be one. How alarming a verdict
 * should look on screen is a display decision this console owns. What the
 * verdict is *called* is not — it is generated from the platform's
 * `PlainLanguage` table, and retyping it here is how the console ends up
 * rendering a word the platform stopped using.
 */
function stateStampVariant(receipt: Receipt): StampVariant {
  switch (receipt.state) {
    case 'confirmed':
      return 'ok'
    case 'needs_you':
      return 'warn'
    case 'mismatch':
    case 'refused':
      return 'stop'
    default:
      return 'open'
  }
}

type EvidenceDetail = { kind: 'row'; row: EventRow } | { kind: 'check'; check: Check } | null

function findEvidenceDetail(receipt: Receipt, evidence: DeclaredStep['evidence']): EvidenceDetail {
  if (evidence === null) return null
  if (evidence.kind === 'row') {
    const row = receipt.rows.find((r) => r.seq === evidence.seq)
    return row ? { kind: 'row', row } : null
  }
  const check = receipt.checks.find((c) => c.seq === evidence.seq)
  return check ? { kind: 'check', check } : null
}

function StepRow({ step, receipt }: { step: DeclaredStep; receipt: Receipt }) {
  const evidence = findEvidenceDetail(receipt, step.evidence)

  return (
    <div style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--rule)' }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted)' }}>
          {String(step.order).padStart(2, '0')}
        </span>
        <span>{step.intent}</span>
        <Stamp variant={step.status === 'evidenced' ? 'ok' : 'warn'} />
      </div>
      {evidence?.kind === 'row' && (
        <p
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-min-mono)',
            color: 'var(--muted)',
          }}
        >
          occurred {formatInstant(evidence.row.occurredAt)} · recorded{' '}
          {formatInstant(evidence.row.recordedAt)}
          {evidence.row.driftNote !== null && <> — {evidence.row.driftNote}</>}
        </p>
      )}
      {evidence?.kind === 'check' && (
        <p
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-min-mono)',
            color: 'var(--muted)',
          }}
        >
          {evidence.check.statement}
          {evidence.check.reason !== null && <> — {evidence.check.reason}</>} · observed{' '}
          {evidence.check.observedAt}
        </p>
      )}
      {step.status === 'missing' && (
        <p
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-min-mono)',
            color: 'var(--warn)',
          }}
        >
          No evidence yet.
        </p>
      )}
    </div>
  )
}

function TriptychSection({ triptych }: { triptych: NonNullable<Triptych> }) {
  return (
    <>
      <HairlineRule />
      <section>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem' }}>The triptych</h2>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {(['expected', 'claimed', 'observed'] as const).map((panelKey) => {
            const panel = triptych[panelKey]
            return (
              <div key={panelKey} style={{ flex: '1 1 12rem', minWidth: '12rem' }}>
                <p
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 'var(--text-min-mono)',
                    textTransform: 'uppercase',
                  }}
                >
                  {panelKey} · {panel.epistemic}
                </p>
                <p style={{ color: 'var(--muted)' }}>{panel.summary}</p>
                <dl>
                  {panel.facts.map((fact) => (
                    <div key={fact.label}>
                      <dt
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: 'var(--text-min-mono)',
                          color: 'var(--muted)',
                        }}
                      >
                        {fact.label}
                      </dt>
                      <dd>{fact.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )
          })}
        </div>
      </section>
    </>
  )
}

function ReceiptView({
  receipt,
  asOf,
  onRefresh,
}: {
  receipt: Receipt
  asOf: string
  onRefresh: () => void
}) {
  const verdicts = useVerdicts()
  const variant = stateStampVariant(receipt)
  const label = verdictLabel(verdicts.data, receipt.state)
  const offered = receipt.availableActions
  const approveOffered = offered.some((a) => a.action === 'approve')
  const refuseOffered = offered.some((a) => a.action === 'refuse')
  const ruleOffered = offered.find((a) => a.action === 'rule')
  const navigate = useNavigate()

  return (
    <main style={{ padding: '2rem', maxWidth: '48rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--muted)' }}>← Runs</span>
        <AsOfLine asOf={asOf} onRefresh={onRefresh} />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginTop: '1rem',
        }}
      >
        <div>
          {/*
            THE TITLE WHEN THERE IS NO CUSTOMER — `LGH-024`.

            This was `customer ?? receipt.runId`, and on every real platform
            run the customer is null — so the page's `<h1>` was a raw UUID,
            with the SAME id repeated as `Run 4f466c…dcdf55` directly beneath
            it. An operations manager arriving from *"Doesn't match — needs
            you"* met a wall of hex, twice, and no name.

            **The short form is the honest title when nothing else names the
            run.** It is what the queue and the Runs list already call it, so
            the operator arrives at a page titled the thing they clicked. The
            full id stays reachable — `Fingerprint` carries it as a `title`
            attribute, and the receipt download is named with it.

            Not invented: when the platform DOES send a customer, that is
            still the title, unchanged.
          */}
          <h1 style={{ marginBottom: '0.25rem' }}>
            {receipt.addressedTo?.customer ?? (
              <>
                Run <Fingerprint value={receipt.runId} />
              </>
            )}
            {/* Addendum §H: the run's own evidence-tier amount, not a field
                inside a signed receipt artifact — this view is the operator
                looking at their own business, not the published record. */}
            {formatMoney(receipt.amount) !== null && (
              <span style={{ color: 'var(--muted)' }}> · {formatMoney(receipt.amount)}</span>
            )}
          </h1>
          {/* Only when the title is the customer — otherwise this is the
              title again, in a smaller font. */}
          {receipt.addressedTo !== null && (
            <p
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-min-mono)',
                color: 'var(--muted)',
              }}
            >
              Run <Fingerprint value={receipt.runId} />
            </p>
          )}
        </div>
        <Stamp variant={variant} label={label} />
      </div>
      <p>{label}</p>

      {/* WHAT CADENCE IS OFFERING, not what this screen assumes.
          `availableActions` comes straight from the contract's `next_actions`,
          so an action nobody can take is not rendered as though they could.

          `Rule` is the exception, and deliberately so: the lead ruled it stays
          501 by design for v1 and that the flow behind it must survive rather
          than be deleted and rebuilt. So it renders DISABLED with the reason
          visible — an operator who looks for it finds it and learns why, which
          a hidden button cannot do. It enables itself the day Cadence puts
          `rule` in `next_actions` (CAD-005); nothing here needs to change. */}
      {(offered.length > 0 || ruleOffered === undefined) && (
        <div style={{ margin: '1rem 0' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {approveOffered && (
              <Button
                variant="primary"
                onClick={() => {
                  navigate(`/runs/${receipt.runId}/approve`)
                }}
              >
                Approve →
              </Button>
            )}
            {refuseOffered && (
              <Button
                variant="danger"
                onClick={() => {
                  navigate(`/runs/${receipt.runId}/refuse`)
                }}
              >
                Refuse →
              </Button>
            )}
            <Button
              variant="secondary"
              disabled={ruleOffered === undefined}
              onClick={() => {
                navigate(`/runs/${receipt.runId}/rule`)
              }}
            >
              Rule →
            </Button>
          </div>
          {ruleOffered === undefined && (
            <p style={{ margin: '0.5rem 0 0', color: 'var(--muted)' }}>
              Ruling on an ambiguity isn’t available from the console yet — that mechanism has its
              own shape, still being designed, separate from approving or refusing.
            </p>
          )}
        </div>
      )}

      <HairlineRule strong />

      <section>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem' }}>Why</h2>
        <p>{receipt.because}</p>
      </section>

      <HairlineRule />

      <section>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem' }}>The steps</h2>
        {receipt.steps.map((step) => (
          <StepRow key={String(step.order)} step={step} receipt={receipt} />
        ))}
      </section>

      {receipt.exceptions.needsADecision.length > 0 && (
        <>
          <HairlineRule />
          <section>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem' }}>
              What we&apos;re waiting on
            </h2>
            {receipt.exceptions.needsADecision.map((exception, index) => (
              <p key={index}>
                {exception.statement !== undefined && <strong>{exception.statement}. </strong>}
                {exception.reason}
              </p>
            ))}
          </section>
        </>
      )}

      {receipt.caveats.length > 0 && (
        <>
          <HairlineRule />
          <section>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem' }}>Caveats</h2>
            {receipt.caveats.map((caveat, index) => (
              <p key={index} style={{ color: 'var(--muted)' }}>
                {caveat}
              </p>
            ))}
          </section>
        </>
      )}

      {receipt.triptych !== null && <TriptychSection triptych={receipt.triptych} />}

      {Object.keys(receipt.resolved).length > 0 && (
        <>
          <HairlineRule />
          <section>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem' }}>
              Referenced content
            </h2>
            {Object.entries(receipt.resolved).map(([key, blob]) => (
              <DisclosureSection key={key} title={key}>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {blob.content}
                </pre>
              </DisclosureSection>
            ))}
          </section>
        </>
      )}
    </main>
  )
}

/**
 * UI spec §4: the complete timeline of one run, rendered from the real
 * receipt — every step shows both clocks and the drift note where present
 * (the two-clock rule), never one, never collapsed.
 */
export function RunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const runQuery = useRun(id ?? '')

  if (id === undefined) {
    return <p role="alert">No run id in the URL.</p>
  }

  if (runQuery.isPending) {
    return (
      <main style={{ padding: '2rem', maxWidth: '48rem' }}>
        <Skeleton width="40%" />
        <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      </main>
    )
  }

  if (runQuery.isError) {
    return (
      <main style={{ padding: '2rem' }}>
        <p role="alert" style={{ color: 'var(--stop)' }}>
          {runQuery.error.message}
        </p>
      </main>
    )
  }

  return (
    <ReceiptView
      receipt={runQuery.data}
      asOf={
        runQuery.dataUpdatedAt
          ? new Date(runQuery.dataUpdatedAt).toISOString()
          : new Date().toISOString()
      }
      onRefresh={() => {
        void runQuery.refetch()
      }}
    />
  )
}
